import React from "react";
import moment from "moment";
import { Box, Typography, Avatar, Button } from "@mui/material";
import { LuEye } from "react-icons/lu";

import TableComponent from "components/grid";
import CustomPagination from "components/CustomPagination";
import StatusBadge from "components/badge/StatusBadge";

import { useGetAllImplementationScheduleQuery } from "store/slices/apis/masterScheduleSlice";

// helper (keep your existing logic if already defined elsewhere)
const getAvatarColor = (text = "") => {
  const colors = ["#7C3AED", "#2563EB", "#059669", "#D97706", "#DC2626", "#0EA5E9"];
  const idx = (text?.charCodeAt(0) || 0) % colors.length;
  return colors[idx];
};

const normalizeSchedules = (scheduleList) => {
  if (!scheduleList) return [];

  // case 1: API returns array directly
  if (Array.isArray(scheduleList)) return scheduleList;

  // case 2: API returns { data: [...] }
  if (Array.isArray(scheduleList?.data)) return scheduleList.data;

  // case 3: API returns { data: { items: [...] } }
  if (Array.isArray(scheduleList?.data?.items)) return scheduleList.data.items;

  // case 4: API returns { items: [...] }
  if (Array.isArray(scheduleList?.items)) return scheduleList.items;

  return [];
};

const ScheduleWorkflowListSection = ({
  scheduleType = "Baseline Schedule",
  onView, // optional callback if parent wants to open view modal
}) => {
  // ---- Sorting ----
  const [sortBy, setSortBy] = React.useState("createdDate");
  const [sortOrder, setSortOrder] = React.useState("desc"); // 'asc' | 'desc'

  const handleRequestSort = (field) => {
    if (!field) return;
    setSortOrder((prev) => (sortBy === field ? (prev === "asc" ? "desc" : "asc") : "asc"));
    setSortBy(field);
  };

  // ---- Pagination ----
  const [pageNumber, setPageNumber] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);

  const handlePageChange = (_, newPage) => setPageNumber(newPage);
  const handlePageSizeChange = (newSize) => {
    const size = Number(newSize) || 10;
    setPageSize(size);
    setPageNumber(1);
  };

  // ---- Query ----
  const { data: scheduleList, isFetching, refetch } =
    useGetAllImplementationScheduleQuery({
      type: scheduleType, // ✅ now using prop
      page: pageNumber,
      pageSize: pageSize,
      sortBy,
      sortOrder,
    });

  // ---- Approved flag (optional) ----
  const [hasApproved, setHasApproved] = React.useState(false);

  React.useEffect(() => {
    const items = normalizeSchedules(scheduleList);

    const approved = items.some((item) => {
      const status = item?.status ?? item?.Status ?? "";
      return typeof status === "string" && status.toLowerCase() === "approved";
    });

    setHasApproved(approved);
  }, [scheduleList]);

  const sortedData = React.useMemo(() => {
    const items = normalizeSchedules(scheduleList);

    return [...items].sort((a, b) => {
      const aValue = a?.[sortBy];
      const bValue = b?.[sortBy];

      if (sortBy === "createdDate") {
        const aTime = aValue ? new Date(aValue).getTime() : 0;
        const bTime = bValue ? new Date(bValue).getTime() : 0;
        return sortOrder === "asc" ? aTime - bTime : bTime - aTime;
      }

      const aNum = Number(aValue);
      const bNum = Number(bValue);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aValue ?? "");
      const bStr = String(bValue ?? "");
      return sortOrder === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [scheduleList, sortBy, sortOrder]);

  const handleViewClick = (row) => {
    // If your parent wants to control view modal, call onView
    if (typeof onView === "function") {
      onView(row);
      return;
    }

    // Otherwise keep minimal safe behavior here
    // (If you have setIsView/setViewData in parent, pass them via onView.)
    console.log("View row:", row);
  };

  const columns = [
    {
      name: "Description",
      field: "description",
      width: "360px",
      renderer: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: getAvatarColor(row?.description),
              color: "#fff",
              fontSize: "14px",
              fontWeight: "bold",
              textTransform: "uppercase",
            }}
          >
            {row?.description?.[0] || "?"}
          </Avatar>

          <Box>
            <Typography
              sx={{
                fontSize: "16px",
                maxWidth: "250px",
                fontWeight: 500,
                fontFamily: "Outfit",
                color: "#433F5E",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={row?.description || ""}
            >
              {row?.description || "N/A"}
            </Typography>
          </Box>

          <Box className="hoverButtons" sx={{ display: "none", gap: "10px" }}>
            <Button
              onClick={() => handleViewClick(row)}
              startIcon={<LuEye style={{ width: 12, height: 12 }} />}
              sx={{
                backgroundColor: "#E7E7EB",
                p: "2px 6px",
                borderRadius: "5px",
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              View
            </Button>
          </Box>
        </Box>
      ),
    },
    {
      name: "Status",
      field: "status",
      renderer: (row) => <StatusBadge status={row?.status ?? row?.Status} />,
    },
    {
      name: "Created Date",
      field: "createdDate",
      renderer: (row) =>
        row?.createdDate ? moment(row.createdDate).format("DD MMM YYYY") : "N/A",
    },
    {
      name: "Created By",
      field: "createdBy",
      renderer: (row) => row?.createdBy ?? "N/A",
    },
  ];

  // total records: support multiple common shapes
  const totalCount =
    scheduleList?.totalRecords ??
    scheduleList?.data?.totalRecords ??
    scheduleList?.data?.total ??
    scheduleList?.total ??
    sortedData.length;

  return (
    <>
      <TableComponent
        data={sortedData}
        isLoading={isFetching}
        columns={columns}
        order={sortOrder}
        orderBy={sortBy}
        createSortHandler={handleRequestSort}
      />

      <CustomPagination
        totalCount={totalCount}
        pageSize={pageSize}
        page={pageNumber}
        onPageChange={handlePageChange}
        setCurrentPage={setPageNumber}
        onPageSizeChange={handlePageSizeChange}
      />
    </>
  );
};

export default ScheduleWorkflowListSection;
